import { Controller } from "@hotwired/stimulus"

// Optimized animations controller with better performance
export default class extends Controller {
  static targets = ["animated", "slide", "dot"]
  static values = { 
    delay: { type: Number, default: 100 },
    slideDuration: { type: Number, default: 5000 },
    autoplay: { type: Boolean, default: true }
  }

  connect() {
    // Use Intersection Observer for better performance
    this.initializeIntersectionObserver()
    
    // Only initialize slideshow if slides exist
    if (this.hasSlideTarget) {
      this.initializeSlideshow()
    }
  }

  initializeIntersectionObserver() {
    // Only animate elements when they come into view
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    }

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry, index) => {
        if (entry.isIntersecting && !entry.target.dataset.animated) {
          // Stagger animations for multiple elements
          setTimeout(() => {
            this.animateElement(entry.target)
          }, index * this.delayValue)
        }
      })
    }, options)

    // Observe all animated elements
    const elements = this.element.querySelectorAll('[data-animate]')
    elements.forEach(el => this.observer.observe(el))
  }

  animateElement(element) {
    // Mark as animated to prevent re-animation
    element.dataset.animated = 'true'
    
    // Add animation class
    element.classList.add('animate-in')
    
    // For AOS-style animations
    if (element.classList.contains('aos-item')) {
      element.style.opacity = '1'
      element.style.transform = 'translateY(0)'
    }
  }

  initializeSlideshow() {
    this.currentSlide = 0
    this.slideInterval = null
    this.isPaused = false
    
    // Show first slide
    this.showSlide(0)
    
    // Start autoplay if enabled
    if (this.autoplayValue && this.slideTargets.length > 1) {
      this.startSlideshow()
    }
    
    // Add dot click handlers if dots exist
    if (this.hasDotTarget) {
      this.dotTargets.forEach((dot, index) => {
        dot.addEventListener('click', () => this.goToSlide(index))
      })
    }
    
    // Pause on hover for better UX
    this.element.addEventListener('mouseenter', () => this.pauseSlideshow())
    this.element.addEventListener('mouseleave', () => this.resumeSlideshow())
    
    // Pause when page is not visible (saves resources)
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.pauseSlideshow()
      } else {
        this.resumeSlideshow()
      }
    })
  }

  showSlide(index) {
    // Hide all slides efficiently
    this.slideTargets.forEach((slide, i) => {
      const isActive = i === index
      slide.classList.toggle('active', isActive)
      
      // Use CSS classes instead of inline styles for better performance
      const img = slide.querySelector('img')
      if (img) {
        img.classList.toggle('opacity-100', isActive)
        img.classList.toggle('opacity-0', !isActive)
      }
    })

    // Update dots if they exist
    if (this.hasDotTarget) {
      this.dotTargets.forEach((dot, i) => {
        dot.classList.toggle('bg-opacity-100', i === index)
        dot.classList.toggle('bg-opacity-50', i !== index)
      })
    }

    this.currentSlide = index
  }

  nextSlide() {
    if (!this.isPaused && this.slideTargets.length > 0) {
      const next = (this.currentSlide + 1) % this.slideTargets.length
      this.showSlide(next)
    }
  }

  goToSlide(index) {
    this.stopSlideshow()
    this.showSlide(index)
    this.startSlideshow()
  }

  startSlideshow() {
    if (this.slideTargets.length > 1 && !this.slideInterval) {
      this.slideInterval = setInterval(() => this.nextSlide(), this.slideDurationValue)
    }
  }

  stopSlideshow() {
    if (this.slideInterval) {
      clearInterval(this.slideInterval)
      this.slideInterval = null
    }
  }

  pauseSlideshow() {
    this.isPaused = true
    this.stopSlideshow()
  }

  resumeSlideshow() {
    this.isPaused = false
    if (this.autoplayValue) {
      this.startSlideshow()
    }
  }

  disconnect() {
    // Clean up observers and intervals
    if (this.observer) {
      this.observer.disconnect()
    }
    
    this.stopSlideshow()
    
    // Remove event listeners
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
  }
}